import { ref } from "vue";
import { GetSalesInvoiceDetail } from "@/api/salesInvoiceApi";
import type { PosPaymentStatus, PosReceiptPayload } from "@/type/pos";
import type { SalesInvoiceDetail } from "@/type/salesInvoice";

/**
 * The response envelope shared by the two POS backend methods.
 *
 * `pet_app.api.pos.create_pos_sale` and `pet_app.api.pos.settle_open_invoice`
 * deliberately answer in the same shape, so one handler covers both paths and
 * they cannot drift apart. Contract: `docs/backend/pos-sale-creation.md`.
 */

export interface PosSaleEnvelope {
  invoice: SalesInvoiceDetail;
  paymentEntryId: string | null;
  /** `null` when the backend returned nothing printable — see `normalizePosReceipt`. */
  receipt: PosReceiptPayload | null;
  settledExistingInvoice: boolean;
  itemCount: number;
  /**
   * true when an `idempotency_key` matched a completed attempt and the backend
   * returned the original sale instead of charging again. Worth telling the
   * cashier: nothing new happened, and the receipt is a reprint.
   */
  replayed: boolean;
}

function toNumber(value: unknown) {
  const numeric = Number(value ?? 0);

  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeText(value: unknown) {
  const normalized = String(value ?? "").trim();

  return normalized || null;
}

/** True only when the backend actually sent a number, so absent stays distinguishable from 0. */
function hasAmount(value: unknown) {
  return value !== undefined && value !== null && value !== "" && Number.isFinite(Number(value));
}

/**
 * Map the backend's receipt onto `PosReceiptPayload`.
 *
 * ⚠️ `printPosReceipt` READS camelCase ONLY (`invoiceId`, `unitPrice`,
 * `balanceDueAmount`, …). A snake_case payload passed straight through prints a
 * receipt of blanks and `NaN` — on a thermal printer, in front of a customer,
 * with no error raised anywhere. Both shapes are accepted here.
 *
 * Returns `null` rather than a half-filled object when there is nothing usable,
 * so the caller can build one from its own state instead of printing rubbish.
 */
export function normalizePosReceipt(raw: any): PosReceiptPayload | null {
  if (!raw || typeof raw !== "object")
    return null;

  const invoiceId = String(raw.invoiceId ?? raw.invoice_id ?? raw.invoice ?? raw.name ?? "").trim();
  const items = Array.isArray(raw.items) ? raw.items : [];

  if (!invoiceId)
    return null;

  const paymentStatus: PosPaymentStatus
    = String(raw.paymentStatus ?? raw.payment_status ?? "Paid") === "Due" ? "Due" : "Paid";

  return {
    invoiceId,
    postingDate: String(raw.postingDate ?? raw.posting_date ?? "").trim(),
    // Only what the backend actually sent. Absent stays absent — the printer drops the
    // Time row rather than invent one, and it falls back to today's date only when
    // `postingDate` above is empty too.
    postingTime: String(raw.postingTime ?? raw.posting_time ?? "").trim() || null,
    customerLabel: String(raw.customerLabel ?? raw.customer_label ?? raw.customer_name ?? "").trim() || null,
    cashierProfileName: String(raw.cashierProfileName ?? raw.cashier_profile_name ?? raw.pos_profile ?? "").trim(),
    paymentStatus,
    paymentMode: String(raw.paymentMode ?? raw.payment_mode ?? "").trim() || null,
    paidAmount: toNumber(raw.paidAmount ?? raw.paid_amount),
    changeAmount: toNumber(raw.changeAmount ?? raw.change_amount),
    balanceDueAmount: toNumber(raw.balanceDueAmount ?? raw.balance_due_amount ?? raw.outstanding_amount),
    subtotalAmount: toNumber(raw.subtotalAmount ?? raw.subtotal_amount ?? raw.net_total),
    discountAmount: toNumber(raw.discountAmount ?? raw.discount_amount),
    discountLabel: String(raw.discountLabel ?? raw.discount_label ?? "").trim() || null,
    totalAmount: toNumber(raw.totalAmount ?? raw.total_amount ?? raw.grand_total),
    // Recorded, not yet printed. `totalAmount` above stays the gross so existing receipt
    // templates are byte-identical; these are here so one can opt into showing that part of
    // the bill was already paid on a boarding stay. Left null when absent rather than
    // defaulted to 0 — "no advance" and "the backend never said" must stay distinguishable.
    totalAdvanceAmount: hasAmount(raw.totalAdvance ?? raw.total_advance)
      ? toNumber(raw.totalAdvance ?? raw.total_advance)
      : null,
    amountDueAmount: hasAmount(raw.amountDue ?? raw.amount_due)
      ? toNumber(raw.amountDue ?? raw.amount_due)
      : null,
    // Both spellings accepted here, as everywhere else in this mapper — the
    // backend's own key is snake_case and the printer only reads camelCase.
    partnerLabel: String(
      raw.partnerLabel ?? raw.partner_label ?? raw.delivery_partner_name ?? raw.delivery_partner ?? "",
    ).trim() || null,
    partnerOrderRef: String(raw.partnerOrderRef ?? raw.partner_order_ref ?? "").trim() || null,
    items: items.map((item: any) => {
      const qty = toNumber(item?.qty);
      const unitPrice = toNumber(item?.unitPrice ?? item?.unit_price ?? item?.rate);

      return {
        label: String(item?.label ?? item?.item_name ?? item?.itemName ?? item?.item_code ?? "").trim(),
        qty,
        unitPrice,
        amount: toNumber(item?.amount ?? qty * unitPrice),
      };
    }),
  };
}

/**
 * Turn the raw envelope into something the POS page can use.
 *
 * ⚠️ THE INVOICE IS RE-READ RATHER THAN TRUSTED. `SalesInvoiceDetail` is this
 * app's MAPPED camelCase shape; what a whitelisted method returns is a raw
 * Frappe document, so passing it through hands the caller an object whose `.id`,
 * `.total` and `.items` are all `undefined` — and `sell.vue` reads `.id` for the
 * receipt and the success message. One extra read buys a document correct by
 * construction, and picks up whatever the submit recalculated server-side.
 */
export async function resolvePosSaleEnvelope(
  raw: any,
  fallbackInvoiceId: string,
  fallbackItemCount: number,
): Promise<PosSaleEnvelope> {
  const invoiceId = String(
    raw?.created_invoice
    ?? raw?.createdInvoice
    ?? raw?.invoice?.name
    ?? raw?.invoice?.id
    ?? raw?.invoice_id
    ?? raw?.invoiceId
    ?? fallbackInvoiceId,
  ).trim();

  if (!invoiceId)
    throw new Error("The sale completed, but the backend named no invoice.");

  const invoice = await GetSalesInvoiceDetail(ref(false), invoiceId);
  if (!invoice)
    throw new Error(`Invoice ${invoiceId} was created, but it could not be read back.`);

  return {
    invoice,
    paymentEntryId: normalizeText(raw?.payment_entry_id ?? raw?.paymentEntryId),
    receipt: normalizePosReceipt(raw?.receipt),
    settledExistingInvoice: Boolean(raw?.settled_existing_invoice ?? raw?.settledExistingInvoice ?? false),
    itemCount: Number(
      raw?.item_count
      ?? raw?.itemCount
      ?? raw?.appended_item_count
      ?? raw?.appendedItemCount
      ?? fallbackItemCount,
    ),
    replayed: Boolean(raw?.replayed),
  };
}

/**
 * A key that lets a retry of the SAME sale be recognised rather than charged twice.
 *
 * ⚠️ `crypto.randomUUID()` is only defined in a SECURE CONTEXT, and this app is
 * served over plain HTTP — calling it unguarded throws and takes the checkout
 * down with it. Guarded exactly the way the rest of the repo does
 * (`FrappeConditionBuilder.vue:286`); the fallback needs to be unique per
 * attempt, not cryptographically strong.
 */
export function newIdempotencyKey(prefix = "pos") {
  // The prefix only reaches the fallback: a UUID needs no namespace, and the default keeps
  // every existing POS key byte-identical to before this took a parameter.
  return globalThis.crypto?.randomUUID?.()
    ?? `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
